import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { useBotConfigStore } from "../../../Store/Slices/ChatbotSlices/BotConfigSlice";
import AddIcon from "@mui/icons-material/Add"; // Assuming MUI for the "+" icon
import { allMessagesStore, removeAllMessagesForLanguage, setBotLanguage, setDisableHeaderSelection, setLanguageSelectionPopup, setLanguageSelector } from "../../../Store/Slices/socket/AllMessagesSlice";
import { HostWindowCommService } from "../../../Services/IframeHostService/HostWindowCommService";
import { OEvents } from "../../../Services/IframeHostService/models";

const AppStartScreenLanguageSelector = ({onComplete}: {onComplete: () => void}) => {
    const { botConfig } = useAppSelector(useBotConfigStore);
    const [languageData, setLanguageData] = useState<any>([]);
    const { botLanguage } = useAppSelector(allMessagesStore);
    const dispatch = useAppDispatch();
    const [showMore, setShowMore] = useState(false);
    let commService = React.useMemo(() => {
        return HostWindowCommService.getInstance();
      }, []);
    const chatbotLanguages = useMemo(
      () =>{
        return Array.isArray(botConfig?.chatbot?.chatbot_languages) ?
          botConfig?.chatbot?.chatbot_languages?.filter((language: any) => language?.isActive) :
          [];
      },[botConfig]
    );
  
    const handleLanguageClick = (language: any) => {
      dispatch(setBotLanguage(language?.value || "english"));
      commService.postMessage({
        event_type: OEvents.CHATBOT_SESSION_ID,
        data: null,
      });
      dispatch(removeAllMessagesForLanguage());
      dispatch(setLanguageSelector(false));
      onComplete();
    };

    const onClickMore = () => {
        setShowMore(true);
        dispatch(setLanguageSelectionPopup(true))
    }
    if (chatbotLanguages.length === 0) {
      dispatch(setBotLanguage("english"));
      return null; 
    }

    const normalizedLanguages = [
      { label: "English", isActive: true, name: "English", value: "english" },
      ...(chatbotLanguages?.filter((lang: any) => lang.value !== "english") || [])
    ];
      return (
        <StyledLanguageSelectorWrapper>
            <LanguageDescription>Please choose a language of your preference.</LanguageDescription>
            <ButtonsWrapper>
              {[
                { label: "English", isActive: true, name:"English", value: "english" },
                ...(chatbotLanguages?.filter((lang: any) => lang.value !== "english") || [])
              ]
                .slice(0, 3)
                .map((language: any, index: number) => (
                  <LanguageButton
                    key={language.value}
                    botConfig={botConfig?.theme}
                    onClick={() => handleLanguageClick(language)}
                  >
                    {language?.label}
                  </LanguageButton>
                ))}
                {normalizedLanguages.length > 3 && (
                    <>
                        {normalizedLanguages.length > 3 && (
                            <MoreButton onClick={onClickMore} botConfig={botConfig?.theme}>
                                <AddIcon />
                            </MoreButton>
                        )}
                    </>
                )}
            </ButtonsWrapper>
        </StyledLanguageSelectorWrapper>
    );
};

export default AppStartScreenLanguageSelector;

const LanguageDescription = styled.p`
  font-size: 14px;
  color: grey;
  text-align: center;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  align-items: center;
`;

const LanguageButton = styled.button<{ botConfig: any }>`
  padding: 10px 20px;
  border-radius: ${({ theme }) =>
    theme?.industry_style?.enabled ? theme?.industry_style?.buttonRadius : "19px"};
  border: 0px;
  background-color: ${props => props.botConfig?.button?.background || "#fff"};
  color: ${props => props.botConfig?.button?.text || "#000"};
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${props => props.botConfig?.button_hover?.background || "#f0f0f0"};
    color: ${props => props.botConfig?.button_hover?.text || "#000"};
    border: 1px solid ${props => props.botConfig?.button?.background || "#f0f0f0"};
  }
`;

const MoreButton = styled.button<{ botConfig: any }>`
  padding: 8px;
  border-radius: 50%;
  border: 0px;
  background-color: ${props => props.botConfig?.button?.background || "#fff"};
  color: ${props => props.botConfig?.button?.text || "#000"};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  transition: background-color 0.3s ease;
  font-size: 12px;

  &:hover {
    background-color: ${props => props.botConfig?.button_hover?.background || "#f0f0f0"};
    color: ${props => props.botConfig?.button_hover?.text || "#000"};
  }
`;

const AdditionalLanguagesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  justify-content: center;
`;

const StyledLanguageSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  height: 20%;
  background-color: #fff;
  padding: 10px;  
`;